/**
 * @fileoverview React Query hooks for fetching reference data (lookup tables)
 * that rarely change and can be cached aggressively. These hooks provide
 * automatic caching, deduplication, and background updates for kinds, formats,
 * categories, subcategories, people, statuses, and tags.
 */

import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { apiClient, type Area, type Category, type Kind, type Person, type Project, type Resource, type Status, type Subcategory } from '@/api';

/**
 * Factory function to create reference data hooks with common pattern.
 * Reduces duplication across similar reference data fetch hooks.
 */
function createReferenceDataHook<T extends { deleted_at?: string | null }>(
  queryKey: string,
  endpoint: string,
  staleTime: number = 10 * 60 * 1000,
  pageSize: number = 1000,
  filterDeleted: boolean = true
): () => UseQueryResult<T[], Error> {
  return function useReferenceData(): UseQueryResult<T[], Error> {
    return useQuery({
      queryKey: [queryKey],
      queryFn: async () => {
        const response = await apiClient.getMany<T>(endpoint, {
          page: '0',
          pageSize: String(pageSize),
        });
        return response.items ?? [];
      },
      staleTime,
      select: filterDeleted
        ? (data) => data.filter((item) => !item.deleted_at)
        : undefined,
    });
  };
}

/**
 * Fetches all kinds (note types). Data is cached for 10 minutes since kinds rarely change.
 */
export const useKinds = createReferenceDataHook<Kind>('kinds', 'kind', 10 * 60 * 1000);

/**
 * Fetches a single kind by name (e.g., "daily", "project").
 */
export function useKindByName(name: string) {
  const { data: kinds, ...query } = useKinds();
  return {
    ...query,
    data: kinds?.find(k => k.name?.toLowerCase() === name.toLowerCase()),
  };
}

/**
 * Fetches all categories. Data is cached for 5 minutes.
 */
export const useCategories = createReferenceDataHook<Category>('categories', 'category', 5 * 60 * 1000, 500);

/**
 * Fetches all subcategories. Data is cached for 5 minutes.
 */
export const useSubcategories = createReferenceDataHook<Subcategory>('subcategories', 'subcategory', 5 * 60 * 1000);

/**
 * Fetches all people, including soft-deleted rows. Notes still resolve
 * person names for historical foreign keys, so deleted records stay in
 * this cache. Hub pages that list people should filter `deleted_at`.
 */
export const usePeople = createReferenceDataHook<Person>('people', 'person', 5 * 60 * 1000, 500, false);

/**
 * Fetches all projects. Data is cached for 5 minutes.
 */
export const useProjects = createReferenceDataHook<Project>('projects', 'project', 5 * 60 * 1000, 1000);

/**
 * Fetches all resources. Data is cached for 5 minutes.
 */
export const useResources = createReferenceDataHook<Resource>('resources', 'resource', 5 * 60 * 1000, 1000);

/**
 * Fetches all areas. Data is cached for 5 minutes.
 */
export const useAreas = createReferenceDataHook<Area>('areas', 'area', 5 * 60 * 1000, 1000);

/**
 * Fetches all statuses, including soft-deleted rows. Notes still resolve
 * status names for historical foreign keys, so deleted records stay in
 * this cache.
 */
export const useStatuses = createReferenceDataHook<Status>('statuses', 'status', 10 * 60 * 1000, 500, false);

/**
 * Hook that fetches all reference data in parallel. Useful for pages that need
 * multiple reference data sets (e.g., edit note page with all dropdowns).
 */
export function useAllReferenceData() {
  const kinds = useKinds();
  const categories = useCategories();
  const subcategories = useSubcategories();
  const people = usePeople();
  const projects = useProjects();
  const resources = useResources();
  const areas = useAreas();
  const statuses = useStatuses();

  const isLoading = kinds.isLoading || categories.isLoading ||
                    subcategories.isLoading || people.isLoading || projects.isLoading ||
                    resources.isLoading || areas.isLoading || statuses.isLoading;

  const error = kinds.error || categories.error ||
                subcategories.error || people.error || projects.error ||
                resources.error || areas.error || statuses.error;

  return {
    kinds: kinds.data ?? [],
    categories: categories.data ?? [],
    subcategories: subcategories.data ?? [],
    people: people.data ?? [],
    projects: projects.data ?? [],
    resources: resources.data ?? [],
    areas: areas.data ?? [],
    statuses: statuses.data ?? [],
    isLoading,
    error,
  };
}
